import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CircleReview {
  id: string;
  userId: string;
  userName: string;
  userUsername: string;
  userImage: string;
  artistName: string;
  rating: number;
  content: string;
  likes: number;
  date: string;
  concertDate: string | null;
  tourName: string | null;
  tourId: string | null;
  venue: string | null;
  createdAt: string;
}

export interface HomeCircleData {
  followingCount: number;
  reviews: CircleReview[];
}

async function fetchCircleReviews(userId: string): Promise<HomeCircleData> {
  const { data: following } = await supabase
    .from('followers')
    .select('following_id')
    .eq('follower_id', userId);

  const followingIds = (following || []).map((f: any) => f.following_id);
  if (followingIds.length === 0) return { followingCount: 0, reviews: [] };

  const { data: reviews } = await supabase
    .from('reviews')
    .select(`*, profiles!inner(username, name, profile_picture)`)
    .eq('status', 'published')
    .in('user_id', followingIds)
    .order('created_at', { ascending: false })
    .limit(6);

  const mapped: CircleReview[] = (reviews || []).map((r: any) => ({
    id: r.id,
    userId: r.user_id,
    userName: r.profiles.name,
    userUsername: r.profiles.username,
    userImage: r.profiles.profile_picture || '/placeholder.svg',
    artistName: r.artist_name,
    rating: r.rating,
    content: r.content,
    likes: r.likes || 0,
    date: String(r.created_at).split('T')[0],
    concertDate: r.concert_date,
    tourName: r.tour_name,
    tourId: r.tour_id,
    venue: r.show_location,
    createdAt: r.created_at,
  }));

  return { followingCount: followingIds.length, reviews: mapped };
}

/**
 * "From Your Circle" reviews for Home, as cached server state.
 * Keyed per user so a sign-out / user switch can never reuse another feed.
 */
export const useHomeCircleReviews = (userId?: string) =>
  useQuery({
    queryKey: ['home', 'circle-reviews', userId ?? 'anon'],
    queryFn: () => fetchCircleReviews(userId as string),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Social feed: refresh in the background on a deliberate revisit once
    // stale. Cached reviews stay on screen, so no blanking or layout jump.
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
